import { z } from 'zod';
import { EmailDomain } from './support.js';
import { PersonRef } from './people.js';
import { FileInfo } from './files.js';
import { PlanPeriodKind } from './helpdesk.js';
export const ProjectStatus = z.enum(['on_track', 'at_risk', 'waiting', 'paused', 'done']);
export const ProjectKind = z.enum(['retainer', 'project', 'support']);
export const ProjectKey = z.string().regex(/^[A-Z][A-Z0-9]{1,5}$/, '2–6 capital letters or digits, starting with a letter');
export const Client = z.object({
    id: z.uuid(),
    name: z.string(),
    color: z.number().int().min(0).max(7),
    parentClientId: z.uuid().nullable(),
    archived: z.boolean(),
    /** Email from these domains becomes this client's tickets. */
    emailDomains: z.array(z.string()),
});
export const CreateClientInput = z.object({
    name: z.string().trim().min(1).max(80),
    parentClientId: z.uuid().nullable().optional(),
});
export const UpdateClientInput = z.object({
    name: z.string().trim().min(1).max(80).optional(),
    archived: z.boolean().optional(),
    emailDomains: z.array(EmailDomain).max(20).optional(),
});
export const Project = z.object({
    id: z.uuid(),
    clientId: z.uuid(),
    name: z.string(),
    key: ProjectKey,
    kind: ProjectKind,
    status: ProjectStatus,
    retainerHours: z.number().nullable(),
    description: z.string(),
    nextMilestone: z.string().nullable(),
    dueDate: z.string().nullable(),
    archived: z.boolean(),
    channelId: z.uuid().nullable(),
});
export const CreateProjectInput = z.object({
    clientId: z.uuid(),
    name: z.string().trim().min(1).max(80),
    key: ProjectKey,
    kind: ProjectKind.default('project'),
    retainerHours: z.number().min(0).max(1000).nullable().optional(),
    description: z.string().max(20000).optional(),
    dueDate: z.iso.date().nullable().optional(),
});
export const UpdateProjectInput = z.object({
    name: z.string().trim().min(1).max(80).optional(),
    status: ProjectStatus.optional(),
    kind: ProjectKind.optional(),
    retainerHours: z.number().min(0).max(1000).nullable().optional(),
    description: z.string().max(20000).optional(),
    nextMilestone: z.string().max(140).nullable().optional(),
    dueDate: z.iso.date().nullable().optional(),
    archived: z.boolean().optional(),
    memberUserIds: z.array(z.uuid()).max(200).optional(),
});
/** The open task to do next on a project (Projects list "Next up"): the soonest due, then the highest priority. */
export const ProjectNextUp = z.object({ id: z.uuid(), key: z.string(), title: z.string(), dueDate: z.string().nullable() });
export const ProjectRow = z.object({
    project: Project,
    openTasks: z.number().int(),
    openSupport: z.number().int(),
    team: z.array(PersonRef),
    lastActivityAt: z.string().nullable(),
    progress: z.number().min(0).max(1),
    /** Missing from an older API; null when nothing is open. */
    nextUp: ProjectNextUp.nullable().optional(),
});
export const ProjectsIndex = z.object({
    stats: z.object({
        activeProjects: z.number().int(),
        clients: z.number().int(),
        openTasks: z.number().int(),
        dueThisWeek: z.number().int(),
        openSupport: z.number().int(),
        slaAtRisk: z.number().int(),
        hoursThisMonth: z.number(),
        hoursPlanned: z.number(),
    }),
    groups: z.array(z.object({ client: Client, projects: z.array(ProjectRow) })),
});
export const ClientList = z.object({ items: z.array(Client) });
/**
 * A project's members (docs/adr/0074): they see the project, its channel and its work. Owners and admins see every
 * project without being on it and are the only ones who change this list (`canManage`).
 */
export const ProjectMembers = z.object({ items: z.array(PersonRef), canManage: z.boolean() });
export const AddProjectMembersInput = z.object({ userIds: z.array(z.uuid()).min(1).max(200) });
/**
 * A project's files (Project page → Files, docs/adr/0078): ready files attached to its tasks and tickets, or posted in its
 * channel or in messages tagged with it, that the caller can open. Newest first.
 */
export const ProjectFiles = z.object({ items: z.array(FileInfo) });
// --- Clients section (docs/adr/0078) ----------------------------------------------------------------------
/**
 * Someone at the client: a client guest with a Chapsi account (in one of the client's channels), or a support contact
 * (email-to-ticket and the client portal, only while the Support desk is on). Emails only for owners and admins.
 */
export const ClientContact = z.object({
    id: z.uuid(),
    kind: z.enum(['guest', 'contact']),
    name: z.string().nullable(),
    email: z.string().nullable(),
});
/** The client's support plan in one line: hours per period and this period's use. Null without a plan or with Support off. */
export const ClientPlanSummary = z.object({
    hours: z.number(),
    period: PlanPeriodKind,
    usedMinutes: z.number().int().nullable(),
    allowanceMinutes: z.number().int().nullable(),
    percent: z.number().int().nullable(),
    /** Exclusive: the plan renews on this date. */
    periodEnd: z.string().nullable(),
});
// --- Client health (v4 RESTYLE_BRIEF.md finding 9, UI-12; docs/adr/0084) ----------------------------------
export const ClientHealth = z.enum(['on_track', 'at_risk', 'overdue']);
export const ClientHealthReason = z.enum(['overdue_tasks', 'sla_breached', 'sla_at_risk', 'over_plan', 'near_plan']);
/** Hours used this month at or above this share of the plan is "at risk"; above the plan is "overdue". */
export const CLIENT_HOURS_AT_RISK = 0.8;
/**
 * How a client is doing, worked out by the API from what the caller may see (docs/adr/0074): the open work and hours of
 * the client's projects visible to them. Hours are counts, never money (rates and amounts stay with Invoicing).
 *
 * - `openTasks` / `openTickets`: open tasks (not tickets) and open support tickets; null while Tasks / Support is off.
 * - `plan`: what the client is on: retainer projects (their monthly hours added up), a support plan, plain projects, or nothing.
 * - `hours`: minutes logged this month on those projects, and the plan's minutes when there is a monthly plan; null while
 *   Time is off.
 * - `team`: the people on the client's live projects, once each.
 */
export const ClientHealthSummary = z.object({
    health: ClientHealth,
    reasons: z.array(ClientHealthReason),
    overdueTasks: z.number().int(),
    slaBreached: z.number().int(),
    slaAtRisk: z.number().int(),
    openTasks: z.number().int().nullable(),
    openTickets: z.number().int().nullable(),
    plan: z.object({ kind: z.enum(['retainer', 'support', 'project', 'none']), hours: z.number().nullable() }),
    hours: z.object({ usedMinutes: z.number().int(), planMinutes: z.number().int().nullable() }).nullable(),
    team: z.array(PersonRef),
});
/**
 * The health rule (one place, shared by the API and its tests): overdue when a task is past its due date, an SLA is
 * breached or the month's hours are over the plan; at risk when an SLA is due within the warning window or the hours
 * reach 80% of the plan; otherwise on track.
 */
export function clientHealth(f) {
    const reasons = [];
    const share = f.usedMinutes !== null && f.planMinutes ? f.usedMinutes / f.planMinutes : null;
    if (f.overdueTasks > 0)
        reasons.push('overdue_tasks');
    if (f.slaBreached > 0)
        reasons.push('sla_breached');
    if (share !== null && share > 1)
        reasons.push('over_plan');
    if (f.slaAtRisk > 0)
        reasons.push('sla_at_risk');
    if (share !== null && share <= 1 && share >= CLIENT_HOURS_AT_RISK)
        reasons.push('near_plan');
    const overdue = reasons.some((r) => r === 'overdue_tasks' || r === 'sla_breached' || r === 'over_plan');
    return { health: overdue ? 'overdue' : reasons.length ? 'at_risk' : 'on_track', reasons };
}
/**
 * One client as the Clients section shows it: its general channel (when the caller may read it), the projects the caller
 * may see (archived ones too, flagged), the people at the client and the support plan.
 */
export const ClientOverview = z.object({
    client: Client,
    channel: z.object({ id: z.uuid(), name: z.string() }).nullable(),
    projects: z.array(ProjectRow),
    /** The first few in the list; all of them on the client page. */
    contacts: z.array(ClientContact),
    contactCount: z.number().int(),
    supportPlan: ClientPlanSummary.nullable(),
    /** Health, plan, open work, hours this month and team (UI-12, UI-13). Missing from an older API. */
    health: ClientHealthSummary.optional(),
});
/** `canManage`: owners and admins create and edit clients and their projects; members read. */
export const ClientsOverview = z.object({ items: z.array(ClientOverview), canManage: z.boolean() });
export const ClientDetail = ClientOverview.extend({ canManage: z.boolean() });
//# sourceMappingURL=projects.js.map